import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import { ColumnDefinition } from 'src/app/shared/model/column-definition';
import { TranslateService } from '@ngx-translate/core';
import { toFileNameDateTimeFormat, toVisibleDateTimeFormat } from 'src/app/shared/util/date-util';
import { ExcelWorkerOutput, PdfWorkerOutput } from '../model/data-print';
import { BehaviorSubject } from 'rxjs';
import { UserSettingsService } from 'src/app/settings/service/user-settings.service';
import { stripHtml } from '../util/html-utils';

interface ColumnMeta {
  columns: ColumnDefinition[];
  lang: string;
  visibleColumns: ColumnDefinition[];
  rowNumberKey: string;
  columnKeys: string[];
}

@Injectable({
  providedIn: 'root'
})
export class DataPrintService {

  private pdfWorker?: Worker;
  private excelWorker?: Worker;
  private pdfBusy = new BehaviorSubject(false);
  private excelBusy = new BehaviorSubject(false);
  private excelProgress = new BehaviorSubject(0);
  private pdfProgress = new BehaviorSubject(0);
  isPdfBusy$ = this.pdfBusy.asObservable();
  pdfProgress$ = this.pdfProgress.asObservable();
  isExcelBusy$ = this.excelBusy.asObservable();
  excelProgress$ = this.excelProgress.asObservable();
  readonly enabled: boolean;

  private columnMetaCache: ColumnMeta | null = null;

  constructor(private translate: TranslateService, private settings: UserSettingsService) {
    if (typeof Worker === 'undefined') {
      console.error('Web Workers not supported');
      return;
    } else if (this.settings.isTablePrintEnabled()) {
      this.pdfWorker = new Worker(new URL('./pdf.worker', import.meta.url));
      this.excelWorker = new Worker(new URL('./excel.worker', import.meta.url));
      this.enabled = true;
    }
  }

  get isPdfBusy(): boolean { return this.pdfBusy.value; };
  get isExcelBusy(): boolean { return this.excelBusy.value; };

  async exportToPdf<T>(data: T[], columns: ColumnDefinition[], fileName?: string, title?: string): Promise<void> {
    if (!this.enabled) return Promise.reject('Service disabled');
    if (this.pdfBusy.getValue()) return;

    this.pdfProgress.next(0);
    this.pdfBusy.next(true);
    let printable: Record<string, string>[];
    let headerKeys: string[];
    try {
      printable = this.toPrintableRows(data, columns);
      headerKeys = printable.length > 0 ? Object.keys(printable[0]) : [];

    } catch (error) {
      this.pdfBusy.next(false);
      throw error;
    }

    return new Promise((resolve, reject) => {

      this.pdfWorker.postMessage({
        printable,
        headerKeys,
        title: title ? this.translate.instant(title) : '',
        pageOfTemplate: this.translate.instant('local-print.of'),
        date: toVisibleDateTimeFormat(new Date())
      });

      this.pdfWorker.onmessage = (event: MessageEvent<PdfWorkerOutput>) => {
        if (event.data.type === 'progress') {
          this.pdfProgress.next(event.data.value);
        } else {
          const { success, buffer, error } = event.data;
          if (success) {
            const blob = new Blob([buffer], { type: 'application/pdf' });
            saveAs(blob, `${fileName ? fileName + '_' : ''}${toFileNameDateTimeFormat(new Date())}.pdf`);
            resolve();
            this.pdfProgress.next(100);
          } else {
            console.error('Worker PDF Error:', error);
            reject(error);
          }
          this.pdfBusy.next(false);
        }
      };

      this.pdfWorker.onerror = (err) => {
        console.error('Worker Thread Error:', err);
        this.pdfBusy.next(false);
        reject(err);
      };
    });
  }

  // Zwraca nazwę zapisanego pliku (null, gdy nie było czego zapisywać)
  exportToPdfFromPrintable(printable: Record<string, string>[], fileName?: string, title?: string, onProgress?: (pct: number) => void): Promise<string | null> {
    if (!this.enabled) return Promise.reject('Service disabled');
    if (this.pdfBusy.getValue()) return Promise.reject('Busy');
    if (!printable.length) return Promise.resolve(null);

    this.pdfProgress.next(0);
    this.pdfBusy.next(true);
    const headerKeys = Object.keys(printable[0]);

    return new Promise((resolve, reject) => {
      this.pdfExportReject = reject;
      this.pdfWorker.postMessage({
        printable,
        headerKeys,
        title: title ? this.translate.instant(title) : '',
        pageOfTemplate: this.translate.instant('local-print.of'),
        date: toVisibleDateTimeFormat(new Date())
      });
      this.pdfWorker.onmessage = (event: MessageEvent<PdfWorkerOutput>) => {
        if (event.data.type === 'progress') {
          this.pdfProgress.next(event.data.value);
          onProgress?.(event.data.value);
        } else {
          this.pdfExportReject = null;
          const { success, buffer, error } = event.data;
          if (success) {
            const blob = new Blob([buffer], { type: 'application/pdf' });
            const savedName = this.buildExportFileName(fileName, 'pdf');
            saveAs(blob, savedName);
            resolve(savedName);
            this.pdfProgress.next(100);
            onProgress?.(100);
          } else {
            reject(error);
          }
          this.pdfBusy.next(false);
        }
      };
      this.pdfWorker.onerror = (err) => {
        this.pdfExportReject = null;
        this.pdfBusy.next(false);
        reject(err);
      };
    });
  }

  async exportToExcel<T>(data: T[], columns: ColumnDefinition[], fileName?: string, title?: string): Promise<string | null> {
    if (!this.enabled) return Promise.reject('Service disabled');
    if (this.excelBusy.getValue()) return null;

    let printable: Record<string, string>[];
    try {
      printable = this.toPrintableRows(data, columns);
    } catch (error) {
      throw error;
    }
    if (!printable.length) return null;

    const headerKeys = Object.keys(printable[0]);
    this.initExcelStream(headerKeys, title ? this.translate.instant(title) : '');
    this.sendExcelChunk(printable);
    return this.finishExcelStream(fileName);
  }

  private pdfExportReject: ((reason?: any) => void) | null = null;
  private excelExportReject: ((reason?: any) => void) | null = null;

  exportToExcelFromPrintable(printable: Record<string, string>[], fileName?: string, title?: string): Promise<string | null> {
    if (!this.enabled) return Promise.reject('Service disabled');
    if (this.excelBusy.getValue()) return Promise.reject('Busy');
    if (!printable.length) return Promise.resolve(null);

    const headerKeys = Object.keys(printable[0]);
    this.initExcelStream(headerKeys, title ? this.translate.instant(title) : '');
    this.sendExcelChunk(printable);
    return this.finishExcelStream(fileName);
  }

  initExcelStream(headerKeys: string[], title?: string): void {
    if (!this.enabled) return;
    this.excelWorker.postMessage({ type: 'init', headerKeys, title: title ?? '' });
  }

  sendExcelChunk(rows: Record<string, string>[]): void {
    if (!this.enabled || !rows.length) return;
    this.excelWorker.postMessage({ type: 'chunk', rows });
  }

  // Zwraca nazwę zapisanego pliku
  finishExcelStream(fileName?: string, onProgress?: (pct: number) => void): Promise<string | null> {
    if (!this.enabled) return Promise.reject('Service disabled');
    this.excelProgress.next(0);
    this.excelBusy.next(true);
    return new Promise((resolve, reject) => {
      this.excelExportReject = reject;
      this.excelWorker.onmessage = (event: MessageEvent<ExcelWorkerOutput>) => {
        if (event.data.type === 'progress') {
          this.excelProgress.next(event.data.value);
          onProgress?.(event.data.value);
        } else {
          this.excelExportReject = null;
          const { success, buffer, error } = event.data;
          if (success) {
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const savedName = this.buildExportFileName(fileName, 'xlsx');
            saveAs(blob, savedName);
            resolve(savedName);
            this.excelProgress.next(100);
            onProgress?.(100);
          } else {
            reject(error);
          }
          this.excelBusy.next(false);
        }
      };
      this.excelWorker.onerror = (err) => {
        this.excelExportReject = null;
        this.excelBusy.next(false);
        reject(err);
      };
      this.excelWorker.postMessage({ type: 'finish' });
    });
  }

  cancelAllExport(): void {
    if (this.pdfWorker) {
      this.pdfWorker.terminate();
      this.pdfWorker = new Worker(new URL('./pdf.worker', import.meta.url));
      this.pdfBusy.next(false);
      if (this.pdfExportReject) { this.pdfExportReject('Cancelled'); this.pdfExportReject = null; }
    }
    if (this.excelWorker) {
      this.excelWorker.terminate();
      this.excelWorker = new Worker(new URL('./excel.worker', import.meta.url));
      this.excelBusy.next(false);
      if (this.excelExportReject) { this.excelExportReject('Cancelled'); this.excelExportReject = null; }
    }
  }

  exportToCsv<T>(data: T[], columns: ColumnDefinition[], fileName?: string): void {
    const printable = this.toPrintableRows(data, columns);
    if (!printable.length) return;
    const csv = '﻿' + this.csvChunkFromPrintable(printable, true);
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `${fileName ? fileName + '_' : ''}${toFileNameDateTimeFormat(new Date())}.csv`);
  }

  csvChunkFromPrintable(rows: Record<string, string>[], includeHeader: boolean): string {
    if (!rows.length) return '';
    const headers = Object.keys(rows[0]);
    const parts: string[] = [];
    if (includeHeader) parts.push(headers.map(DataPrintService.csvEscape).join(','));
    rows.forEach(row => parts.push(headers.map(h => DataPrintService.csvEscape(row[h] ?? '')).join(',')));
    return parts.join('\r\n');
  }

  private static csvEscape(v: string): string {
    const s = String(v ?? '');
    if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  }

  // Zwraca nazwę zapisanego pliku
  saveCsvBlob(chunks: string[], fileName?: string): string {
    const blob = new Blob(chunks, { type: 'text/csv;charset=utf-8;' });
    const savedName = this.buildExportFileName(fileName, 'csv');
    saveAs(blob, savedName);
    return savedName;
  }

  private buildExportFileName(fileName: string | undefined, ext: string): string {
    return `${fileName ? fileName + '_' : ''}${toFileNameDateTimeFormat(new Date())}.${ext}`;
  }

  toPrintableRows<T>(data: T[], columns: ColumnDefinition[], rowOffset = 0): Record<string, string>[] {
    const printableData: Record<string, string>[] = [];
    const { visibleColumns, rowNumberKey, columnKeys } = this.getColumnMeta(columns);
    data.forEach((d, idx) => {
      const element: Record<string, string> = {};
      element[rowNumberKey] = `${rowOffset + idx + 1}.`;
      visibleColumns.forEach((column, colIdx) => {
        const val = d[column.name];
        let value: string = val;

        if (column.localPrintFormat) value = column.localPrintFormat(d);
        else if (column.formatExt) value = column.formatExt(d);
        else value = column.format(val);
        if (value == undefined) value = '';
        if (column.translatable) {
          const str = value.toString().trim();
          value = str !== '' ? this.translate.instant(value.toString()) : value;
        }
        element[columnKeys[colIdx]] = stripHtml(value);
      });
      printableData.push(element);
    });
    return printableData;
  }

  // odfiltrowuje po widocznych i odrzuca kolumny opcji oraz mennu z ikonkami. Jeżeli są jeszcze jakieś do wykluczenia, o których nie wiem można dodać.
  // Wynik jest cache'owany po referencji `columns` i języku, bo eksport wywołuje toPrintableRows raz na każdy chunk
  private getColumnMeta(columns: ColumnDefinition[]): ColumnMeta {
    const lang = this.translate.currentLang;
    if (this.columnMetaCache?.columns === columns && this.columnMetaCache?.lang === lang) {
      return this.columnMetaCache;
    }
    const visibleColumns = columns.filter(c => c.visible && c.name !== 'iconMenu' && c.name !== 'options');
    const rowNumberKey = this.translate.instant('local-print.table-row-number');
    const columnKeys = visibleColumns.map(c => this.translate.instant(c.title || c.name));
    this.columnMetaCache = { columns, lang, visibleColumns, rowNumberKey, columnKeys };
    return this.columnMetaCache;
  }

}
